import React from "react";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import {
  useGetBenefitCategoriesQuery,
  useGetBenefitPartnersQuery,
} from "../../store/services/benefits";
import { benefitStatusOptions } from "../../utils/constants/benefits-constants";
import { BenefitStatus } from "../../models/benefits";
import { DateRangeFilter } from "./DateRangeFilter";

const featuredOptions: { label: string; value: boolean }[] = [
  { label: "Sí", value: true },
  { label: "No", value: false },
];

interface BenefitFiltersProps {
  title: string;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  partnerId?: string;
  setPartnerId: React.Dispatch<React.SetStateAction<string | undefined>>;
  categoryId?: string;
  setCategoryId: React.Dispatch<React.SetStateAction<string | undefined>>;
  status?: BenefitStatus;
  setStatus: React.Dispatch<React.SetStateAction<BenefitStatus | undefined>>;
  featured?: boolean;
  setFeatured: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  vigenciaFrom: Date | null;
  setVigenciaFrom: React.Dispatch<React.SetStateAction<Date | null>>;
  vigenciaTo: Date | null;
  setVigenciaTo: React.Dispatch<React.SetStateAction<Date | null>>;
  onClearAll: () => void;
}

export const BenefitFilters = ({
  title,
  setTitle,
  partnerId,
  setPartnerId,
  categoryId,
  setCategoryId,
  status,
  setStatus,
  featured,
  setFeatured,
  vigenciaFrom,
  setVigenciaFrom,
  vigenciaTo,
  setVigenciaTo,
  onClearAll,
}: BenefitFiltersProps) => {
  // Filtros del listado: usan partners y categorías del contexto actual (el
  // tenant del selector superior). En scope global el listado muestra todos.
  const { data: partners, isLoading: isLoadingPartners } =
    useGetBenefitPartnersQuery({ tenantId: null });
  const { data: categories, isLoading: isLoadingCategories } =
    useGetBenefitCategoriesQuery({ tenantId: null });

  const partnerOptions = React.useMemo(
    () =>
      (partners ?? []).map((partner) => ({
        label: partner.name ?? partner.id,
        value: partner.id,
      })),
    [partners]
  );

  const categoryOptions = React.useMemo(
    () =>
      (categories ?? []).map((category) => ({
        label: category.name ?? category.id,
        value: category.id,
      })),
    [categories]
  );

  return (
    <div className="grid mt-3 row-gap-3">
      {/* Mismo patrón que EventFilters: sin botón "X" por campo (los
          desplegables se limpian con su propio showClear) y un único
          "Limpiar filtros" al final. Fila 1: Título · Partner · Categoría ·
          Estado. Fila 2: Destacado · Vigencia desde · Vigencia hasta. */}
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <InputText
            id="benefitTitle"
            name="benefitTitle"
            className="w-full"
            onChange={(e) => setTitle(e.currentTarget.value)}
            value={title}
          />
          <label htmlFor="benefitTitle">Título</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="benefitPartner"
            name="benefitPartner"
            className="w-full"
            onChange={(e: DropdownChangeEvent) => setPartnerId(e.value ?? undefined)}
            value={partnerId ?? null}
            optionLabel="label"
            optionValue="value"
            options={partnerOptions}
            loading={isLoadingPartners}
            showClear
          />
          <label htmlFor="benefitPartner">Partner</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="benefitCategory"
            name="benefitCategory"
            className="w-full"
            onChange={(e: DropdownChangeEvent) => setCategoryId(e.value ?? undefined)}
            value={categoryId ?? null}
            optionLabel="label"
            optionValue="value"
            options={categoryOptions}
            loading={isLoadingCategories}
            showClear
          />
          <label htmlFor="benefitCategory">Categoría</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="benefitStatus"
            name="benefitStatus"
            className="w-full"
            onChange={(e: DropdownChangeEvent) => setStatus(e.value ?? undefined)}
            value={status ?? null}
            optionLabel="label"
            optionValue="value"
            options={benefitStatusOptions}
            showClear
          />
          <label htmlFor="benefitStatus">Estado</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="benefitFeatured"
            name="benefitFeatured"
            className="w-full"
            onChange={(e: DropdownChangeEvent) => setFeatured(e.value ?? undefined)}
            value={featured ?? null}
            optionLabel="label"
            optionValue="value"
            options={featuredOptions}
            showClear
          />
          <label htmlFor="benefitFeatured">Destacado</label>
        </FloatLabel>
      </div>
      <DateRangeFilter
        variant="filter"
        dateFrom={vigenciaFrom}
        dateTo={vigenciaTo}
        onDateFromChange={setVigenciaFrom}
        onDateToChange={setVigenciaTo}
        startDateId="benefitVigenciaFrom"
        endDateId="benefitVigenciaTo"
        labelFrom="Vigencia desde"
        labelTo="Vigencia hasta"
        columnClassName="col-12 md:col-3"
      />
      <div className="col-12 md:col flex flex-wrap justify-content-end align-items-center gap-2">
        <Button
          type="button"
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          size="small"
          onClick={onClearAll}
        />
      </div>
    </div>
  );
};
